<template>
    <kiosk-inactivity-timeout
        :key="timeoutKey"
        :idle-timeout="timeoutConfig.idleTimeout"
        :idle-timeout-message="timeoutConfig.idleTimeoutMessage"
        :facility-slug="facilitySlug"
    />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useState } from '#app'
import KioskInactivityTimeout from '~/components/Layouts/StorefrontLayout/KioskInactivityTimeout.vue'
import { useRpQuery } from '~/composables/graphql'
import { useLayoutMode } from '~/composables/use-layout-mode'
import { useSelectedLanguage } from '~/composables/use-selected-language'
import { graphql } from '~/resources/graphql'

const FALLBACK_IDLE_TIMEOUT_SECONDS = 60
const facilitySlug = useState<string | undefined>('facilityParamSlug')
const { kioskSlug } = useLayoutMode()
const selectedLanguage = useSelectedLanguage()
const query = useRpQuery({ orgLevel: true })

// Profile URLs retain the kiosk storefront slug but not the facility segment.
const storefrontSlug = computed(() => kioskSlug.value ?? '')
const hasFacilitySlug = computed(() => facilitySlug.value !== undefined)
const { data } = useQuery({
    queryKey: [
        'KioskProfileInactivityTimeoutQuery',
        facilitySlug,
        storefrontSlug,
        selectedLanguage
    ],
    // Middleware supplies the physical facility during normal navigation. A
    // reload has no facility context and intentionally keeps the safe fallback.
    enabled: hasFacilitySlug,
    queryFn: () => {
        const currentFacilitySlug = facilitySlug.value
        if (currentFacilitySlug === undefined) {
            throw new Error('Kiosk facility is unavailable')
        }

        return query(
            graphql(/* GraphQL */ `
                query KioskProfileInactivityTimeoutQuery(
                    $storefrontConfigInput: StorefrontConfigInput!
                ) {
                    storefrontConfig(input: $storefrontConfigInput) {
                        idleTimeout
                        idleTimeoutMessage
                    }
                }
            `),
            {
                storefrontConfigInput: {
                    facilitySlug: currentFacilitySlug,
                    slug: storefrontSlug.value,
                    language: selectedLanguage.value
                }
            }
        )
    }
})

const storefrontConfig = computed(() => data.value?.storefrontConfig)
const timeoutConfig = computed(() => {
    // Fail closed while configuration loads or when the lookup returns null.
    return (
        storefrontConfig.value ?? {
            idleTimeout: FALLBACK_IDLE_TIMEOUT_SECONDS,
            idleTimeoutMessage: ''
        }
    )
})
const timeoutKey = computed(() => {
    // VueUse v11 captures the timeout at setup, so remount when configuration
    // replaces the fail-safe values with the kiosk's configured values.
    return [
        facilitySlug.value,
        timeoutConfig.value.idleTimeout,
        timeoutConfig.value.idleTimeoutMessage
    ].join(':')
})
</script>
